import { Params } from '@angular/router'

import { SECURITY_TAGS } from '@@constants/security-tags'

/**
 * DV-5499 — single source of truth for the portal's business modules.
 *
 * The side navigation and the Business Web Portal modules menu (`/portal/menu`)
 * both render this list, so a module's label, icon, route and permission
 * gating are defined once here. `buildPortalModules()` reproduces the
 * sidenav's historical behaviour exactly (labels, order, visibility rules);
 * `portal-modules.spec.ts` pins that parity.
 */
export enum PortalModuleKey {
    DASHBOARD = 'dashboard',
    ACTIVITIES = 'activities',
    FIELDS = 'fields',
    APPOINTMENTS = 'appointments',
    CLIENTS = 'clients',
    LEADS = 'leads',
    TENANTS = 'tenants',
    POS = 'pos',
    GYM = 'gym',
    EXPENSES = 'expenses',
    REPORTS = 'reports',
    MANAGE_FIELDS = 'manage-fields',
    COMPANY_SETTINGS = 'company-settings'
}

export type PortalModuleSurface = 'sidenav' | 'home'

export type PortalModuleSection = 'main' | 'bottom'

/** Icon tile colours from the In2 Brand Guidelines module icon grid (PRD). */
export const PORTAL_MODULE_COLORS: Record<PortalModuleKey, string> = {
    [PortalModuleKey.DASHBOARD]: '#FF7304',
    [PortalModuleKey.ACTIVITIES]: '#2EAFFF',
    [PortalModuleKey.FIELDS]: '#7FAEB1',
    [PortalModuleKey.APPOINTMENTS]: '#F05E06',
    [PortalModuleKey.CLIENTS]: '#11A2FD',
    [PortalModuleKey.LEADS]: '#5F9598',
    // Tenants is a super-user tool that is not part of the brand icon grid.
    [PortalModuleKey.TENANTS]: '#50585B',
    [PortalModuleKey.POS]: '#C74507',
    [PortalModuleKey.GYM]: '#007BD2',
    [PortalModuleKey.EXPENSES]: '#4B7A7E',
    [PortalModuleKey.REPORTS]: '#0061AA',
    [PortalModuleKey.MANAGE_FIELDS]: '#9E370E',
    [PortalModuleKey.COMPANY_SETTINGS]: '#3E6267'
}

export const COMPANY_LABEL_KEY_PREFIX = 'layout.sidenav.company-labels.'

/** Shape returned by `GeneralService.getCompanyLabels()`. */
export interface PortalCompanyLabels {
    dashboard: string
    activities: string
    fields: string
    appointments: string
    clients: string
    clientsLeads: string
    reports: string
    pos: string
    gym: string
}

export interface PortalModulesContext {
    companyLabels: PortalCompanyLabels
    canViewEvents: boolean
    canViewActivities: boolean
    canViewFields: boolean
    canViewDashboardManager: boolean
    canAccessLeads: boolean
    hasTenant: boolean
    isSuperUser: boolean
}

export interface PortalModuleDef {
    key: PortalModuleKey
    /**
     * Company-label slug (`'dashboard'`, `'clients-leads'`, `'events'`, ...)
     * resolved under `layout.sidenav.company-labels.*`, or — when
     * `labelIsFullKey` is set — a complete translate key.
     */
    label: string
    labelIsFullKey?: boolean
    /** Optional label override for the home surface (same semantics as `label`). */
    homeLabel?: string
    icon: string
    color: string
    /** DOM id for the product walkthrough anchors. */
    id?: string
    routerLink: string
    queryParams?: Params
    /** `undefined` = not tag-gated; `[]` = never granted (hidden for everyone). */
    securityTags?: SECURITY_TAGS[]
    checkVisibility?: boolean
    visible?: boolean
    routerLinkActive?: string
    routerLinkActiveExactStatus?: boolean
    highlightForLeads?: boolean
    section: PortalModuleSection
    surfaces: PortalModuleSurface[]
}

export const getPortalModuleLabelKey = (
    def: PortalModuleDef,
    surface: PortalModuleSurface
): string => {
    const label =
        surface === 'home' && def.homeLabel ? def.homeLabel : def.label

    return def.labelIsFullKey
        ? label
        : COMPANY_LABEL_KEY_PREFIX + label.toLowerCase()
}

export const buildPortalModules = (
    ctx: PortalModulesContext
): PortalModuleDef[] => [
    {
        key: PortalModuleKey.DASHBOARD,
        label: ctx.companyLabels.dashboard,
        icon: 'layout-dashboard',
        color: PORTAL_MODULE_COLORS[PortalModuleKey.DASHBOARD],
        securityTags: [
            SECURITY_TAGS.DASHBOARD_MANAGER,
            SECURITY_TAGS.DASHBOARD_FRONT_DESK
        ],
        routerLink:
            '/portal/dashboard' +
            (ctx.canViewDashboardManager ? '' : '/front-desk'),
        routerLinkActive: 'active',
        routerLinkActiveExactStatus: true,
        section: 'main',
        surfaces: ['sidenav', 'home']
    },
    {
        key: PortalModuleKey.ACTIVITIES,
        label:
            ctx.canViewEvents && !ctx.canViewActivities
                ? 'events'
                : ctx.companyLabels.activities,
        icon: 'ticket',
        id: 'activities-module',
        color: PORTAL_MODULE_COLORS[PortalModuleKey.ACTIVITIES],
        routerLink: '/portal/activities',
        routerLinkActive: 'active',
        routerLinkActiveExactStatus: false,
        checkVisibility: true,
        visible: ctx.canViewActivities || ctx.canViewEvents,
        section: 'main',
        surfaces: ['sidenav', 'home']
    },
    {
        key: PortalModuleKey.FIELDS,
        label: ctx.canViewFields ? ctx.companyLabels.fields : 'rooms',
        icon: 'map',
        color: PORTAL_MODULE_COLORS[PortalModuleKey.FIELDS],
        routerLink: '/portal/fields/calendar',
        routerLinkActive: 'active',
        routerLinkActiveExactStatus: true,
        checkVisibility: true,
        visible: ctx.hasTenant && ctx.canViewFields,
        section: 'main',
        surfaces: ['sidenav', 'home']
    },
    {
        key: PortalModuleKey.APPOINTMENTS,
        label: ctx.companyLabels.appointments,
        icon: 'calendar-check',
        color: PORTAL_MODULE_COLORS[PortalModuleKey.APPOINTMENTS],
        securityTags: [SECURITY_TAGS.APPOINTMENTS_VIEW],
        routerLink: '/portal/appointments/calendar',
        routerLinkActive: 'active',
        routerLinkActiveExactStatus: true,
        section: 'main',
        surfaces: ['sidenav', 'home']
    },
    {
        key: PortalModuleKey.CLIENTS,
        label: ctx.canAccessLeads
            ? ctx.companyLabels.clientsLeads
            : ctx.companyLabels.clients,
        // The home grid has its own Leads card, so it shows plain "Clients".
        homeLabel: ctx.companyLabels.clients,
        icon: 'user',
        color: PORTAL_MODULE_COLORS[PortalModuleKey.CLIENTS],
        securityTags: [SECURITY_TAGS.CLIENTS_VIEW],
        routerLink: '/portal/clients',
        highlightForLeads: true,
        section: 'main',
        surfaces: ['sidenav', 'home']
    },
    {
        key: PortalModuleKey.LEADS,
        label: 'leads',
        icon: 'users',
        color: PORTAL_MODULE_COLORS[PortalModuleKey.LEADS],
        securityTags: [SECURITY_TAGS.ACCESS_LEAD_MODULE],
        routerLink: '/portal/clients',
        queryParams: { tab: 'leads' },
        section: 'main',
        // The sidenav merges Leads into the Clients entry (DV-5069).
        surfaces: ['home']
    },
    {
        key: PortalModuleKey.TENANTS,
        label: 'tenants',
        icon: 'building-2',
        color: PORTAL_MODULE_COLORS[PortalModuleKey.TENANTS],
        routerLink: '/portal/tenants',
        routerLinkActive: 'active',
        routerLinkActiveExactStatus: true,
        checkVisibility: true,
        visible: ctx.isSuperUser,
        section: 'main',
        surfaces: ['sidenav', 'home']
    },
    {
        key: PortalModuleKey.POS,
        label: ctx.companyLabels.pos,
        icon: 'shopping-cart',
        color: PORTAL_MODULE_COLORS[PortalModuleKey.POS],
        securityTags: [SECURITY_TAGS.POS_VIEW],
        routerLink: '/portal/pos',
        routerLinkActive: 'active',
        routerLinkActiveExactStatus: true,
        section: 'main',
        surfaces: ['sidenav', 'home']
    },
    {
        key: PortalModuleKey.GYM,
        label: ctx.companyLabels.gym,
        icon: 'monitor',
        color: PORTAL_MODULE_COLORS[PortalModuleKey.GYM],
        securityTags: [SECURITY_TAGS.GYM_CHECKIN],
        routerLink: '/portal/gym',
        routerLinkActive: 'active',
        routerLinkActiveExactStatus: true,
        section: 'main',
        surfaces: ['sidenav', 'home']
    },
    {
        key: PortalModuleKey.EXPENSES,
        label: 'expenses',
        icon: 'dollar-sign',
        color: PORTAL_MODULE_COLORS[PortalModuleKey.EXPENSES],
        securityTags: [SECURITY_TAGS.VIEW_EXPENSES],
        routerLink: '/portal/expense',
        routerLinkActive: 'active',
        routerLinkActiveExactStatus: true,
        section: 'main',
        surfaces: ['sidenav', 'home']
    },
    {
        key: PortalModuleKey.REPORTS,
        label: 'reports',
        icon: 'file-text',
        color: PORTAL_MODULE_COLORS[PortalModuleKey.REPORTS],
        securityTags: [
            SECURITY_TAGS.REPORTS_FINANCIALS,
            SECURITY_TAGS.REPORTS_ATTENDANCE,
            SECURITY_TAGS.REPORTS_PAYROLL,
            SECURITY_TAGS.REPORTS_REMINDERS_NOTIFICATIONS,
            SECURITY_TAGS.REPORTS_VISITORS,
            SECURITY_TAGS.APPOINTMENTS_VIEW,
            SECURITY_TAGS.POS_VIEW
        ],
        routerLink: '/portal/reports',
        routerLinkActive: 'active',
        routerLinkActiveExactStatus: false,
        section: 'main',
        surfaces: ['sidenav', 'home']
    },
    {
        key: PortalModuleKey.MANAGE_FIELDS,
        label:
            'layout.sidenav.manage.' +
            (ctx.canViewFields
                ? ctx.companyLabels.fields.toLocaleLowerCase()
                : 'rooms'),
        labelIsFullKey: true,
        icon: 'flag',
        color: PORTAL_MODULE_COLORS[PortalModuleKey.MANAGE_FIELDS],
        // Without FIELDS_VIEW the entry is never granted (legacy 'NoManageFields').
        securityTags: ctx.canViewFields
            ? [SECURITY_TAGS.MANAGE_FIELDS_ROOMS]
            : [],
        routerLink: '/portal/fields/manage',
        routerLinkActive: 'active',
        section: 'bottom',
        surfaces: ['sidenav', 'home']
    },
    {
        key: PortalModuleKey.COMPANY_SETTINGS,
        label: 'layout.sidenav.company-settings',
        labelIsFullKey: true,
        icon: 'settings',
        color: PORTAL_MODULE_COLORS[PortalModuleKey.COMPANY_SETTINGS],
        securityTags: [
            SECURITY_TAGS.MANAGE_BASIC_SETTINGS,
            SECURITY_TAGS.MANAGE_STAFF,
            SECURITY_TAGS.MANAGE_BRANCHES,
            SECURITY_TAGS.MANAGE_ONLINE_BOOKING,
            SECURITY_TAGS.MANAGE_PACKAGES_SERVICES,
            SECURITY_TAGS.MANAGE_PAYMENT_METHODS,
            SECURITY_TAGS.MANAGE_DISCOUNTS,
            SECURITY_TAGS.MANAGE_INVENTORY
        ],
        routerLink: '/portal/settings',
        routerLinkActive: 'active',
        section: 'bottom',
        surfaces: ['sidenav', 'home']
    }
]
